import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { TableSkeleton } from '../components/Skeleton';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../hooks/useAuth';
import { Header } from './categoryClusterView/Header';
import { ClusterHealthCard } from './categoryClusterView/ClusterHealthCard';
import { MemberMapCard } from './categoryClusterView/MemberMapCard';
import { ConfusabilityCard } from './categoryClusterView/ConfusabilityCard';
import { MembersSection } from './categoryClusterView/MembersSection';
import { ClusterBulkSidebar } from './categoryClusterView/ClusterBulkSidebar';
import { RetrainBanner } from './categoryClusterView/RetrainBanner';
import { useClusterMemberActions } from './categoryClusterView/useClusterMemberActions';

export default function CategoryClusterView() {
  const { setTitle } = useDocumentTitle('Category Cluster');
  const { id } = useParams<{ id: string }>();
  const [selectedRows, setSelectedRows] = useState(new Set<string>());
  const handleDeselectAll = useCallback(() => setSelectedRows(new Set()), []);

  const clusterQuery = useQuery({
    queryKey: ['categoryCluster', id],
    queryFn: () => backend.getCategoryCluster(id!).then((res) => res.data),
    enabled: !!id,
  });

  const item = clusterQuery.data?.item;
  const categoryId = item?.categoryId;

  const auth = useAuth();
  const canCurate = auth.hasAccess(['admin', 'supervisor', 'curator']);
  const canReclassify = auth.hasAccess(['admin', 'curator']);
  const memberActions = useClusterMemberActions({
    clusterId: id ?? '',
    categoryId,
    categoryName: item?.categoryName,
    selectedIds: selectedRows,
    onDone: handleDeselectAll,
  });
  const showSidebar = canCurate && selectedRows.size > 0;

  // Sibling clusters in the same category drive prev/next navigation (and Task 9's
  // sibling bars).
  const siblingsQuery = useQuery({
    queryKey: ['categoryClusters', categoryId?.toString()],
    queryFn: () => backend.listCategoryClusters({ categoryId: categoryId!.toString() }),
    enabled: categoryId != null,
  });

  useEffect(() => {
    if (item?.categoryName) {
      setTitle(`${item.categoryName} Cluster #${id} - Cluster View`);
    }
  }, [item?.categoryName, id]);

  // Prev/Next navigates to a new :id without remounting this component, so clear
  // the member selection on cluster change — otherwise the bulk sidebar would
  // carry page IDs from the previous cluster. (The retrain banner's `dirty` flag
  // is reset inside useClusterMemberActions on the same clusterId change.)
  useEffect(() => {
    handleDeselectAll();
  }, [id, handleDeselectAll]);

  const { prevClusterId, nextClusterId } = useMemo(() => {
    const siblings = (siblingsQuery.data?.items ?? []).map((c) => c.id).sort((a, b) => a - b);
    const idx = siblings.indexOf(Number(id));
    return {
      prevClusterId: idx > 0 ? siblings[idx - 1] : null,
      nextClusterId: idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null,
    };
  }, [siblingsQuery.data, id]);

  const header = (
    <Header
      categoryId={categoryId}
      categoryName={item?.categoryName}
      clusterId={id ?? ''}
      prevClusterId={prevClusterId}
      nextClusterId={nextClusterId}
    />
  );

  if (clusterQuery.isLoading && !clusterQuery.data) {
    return (
      <div>
        {header}
        <TableSkeleton />
      </div>
    );
  }

  if (clusterQuery.error) {
    return (
      <div>
        {header}
        <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow rounded-lg p-6">
          <div className="text-center text-red-600">{(clusterQuery.error as Error).message}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      {memberActions.dirty && <RetrainBanner categoryId={categoryId} categoryName={item?.categoryName} />}
      {item && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ClusterHealthCard item={item} pages={clusterQuery.data!.pages} />
            <MemberMapCard pages={clusterQuery.data!.pages} />
          </div>
          <ConfusabilityCard
            clusterId={id ?? ''}
            categoryName={item.categoryName}
            pages={clusterQuery.data!.pages}
            siblings={siblingsQuery.data?.items ?? []}
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className={showSidebar ? 'lg:col-span-8' : 'lg:col-span-12'}>
              <MembersSection
                pages={clusterQuery.data!.pages}
                categoryName={item.categoryName}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            </div>
            {showSidebar && (
              <ClusterBulkSidebar
                selectedIds={selectedRows}
                onDeselectAll={handleDeselectAll}
                categoryName={item.categoryName}
                pending={memberActions.pending}
                onReclassify={memberActions.reclassify}
                onRelabel={memberActions.relabel}
                onRemove={memberActions.remove}
                canReclassify={canReclassify}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
